import { Button, Input, Space, Tag } from 'antd';
import { useState } from 'react';

import { FacilityDto } from '@clh/api-client';

import { ActionButton } from '../../action-button';
import { useApiClient } from '../../hooks/use-api-client';
import { useSelection } from '../../hooks/use-selection';
import { FetchRecordTable } from '../../table';

const { WEBAPP_AUTH0_CLIENT_ID, WEBAPP_BASE_URL } = ENV_CONFIG_VARS;

type MemberRow = {
    id: string;
    name: string;
    email: string | null;
    status: 'Active' | 'Invited';
};

export default function MembersTab() {
    const api = useApiClient();
    const { selection } = useSelection<FacilityDto>();
    const [inviteEmail, setInviteEmail] = useState<string>('');

    const getRecords = async (): Promise<MemberRow[]> => {
        const [members, invites] = await Promise.all([
            api!.facilityControllerListMembers({
                facilityId: selection.id,
            }),
            api!
                .facilityControllerGetPendingInvites({
                    facilityId: selection.id,
                })
                .catch(() => []),
        ]);

        return [
            ...members.map((m) => ({
                id: m.id,
                name: `${m.firstName} ${m.lastName}`.trim(),
                email: m.email,
                status: 'Active' as const,
            })),
            ...invites.map((i) => ({
                id: i.identityId,
                name: '',
                email: i.email,
                status: 'Invited' as const,
            })),
        ];
    };

    return (
        <FetchRecordTable<MemberRow>
            bordered
            size="small"
            pagination={false}
            getRecords={getRecords}
            cacheKey={'profileControllerListProfiles' + selection.id}
            columns={[
                {
                    title: 'Name',
                    dataIndex: 'name',
                    render: (name: string) => name || '--',
                },
                'email',
                {
                    title: 'Status',
                    dataIndex: 'status',
                    render: (status: MemberRow['status']) =>
                        status === 'Invited' ? (
                            <Tag color="orange">Invited</Tag>
                        ) : (
                            <Tag color="green">Active</Tag>
                        ),
                },
                {
                    title: 'Action',
                    dataIndex: 'id',
                    render: (_id, row) => (
                        <Space>
                            {row.status === 'Active' && (
                                <>
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            window.open(
                                                `${WEBAPP_BASE_URL}/site/impersonate/${row.id}`,
                                                '_blank',
                                                'noreferrer'
                                            )
                                        }
                                    >
                                        Impersonate
                                    </Button>
                                    <ActionButton
                                        size="small"
                                        confirm="Are you sure you want to remove this member"
                                        action={() =>
                                            api!.facilityControllerDeleteFacilityMember(
                                                {
                                                    facilityId: selection.id,
                                                    profileId: row.id,
                                                }
                                            )
                                        }
                                        successMessage="Member removed"
                                    >
                                        Remove
                                    </ActionButton>
                                </>
                            )}
                            {row.status === 'Invited' && (
                                <>
                                    <ActionButton
                                        size="small"
                                        fetchRecordOnAction={false}
                                        action={() =>
                                            api!.facilityControllerInviteUser({
                                                facilityId: selection.id,
                                                inviteCreateDto: {
                                                    emails: [row.email!],
                                                    clientId:
                                                        WEBAPP_AUTH0_CLIENT_ID,
                                                },
                                            })
                                        }
                                        successMessage="Invite re-sent"
                                    >
                                        Resend
                                    </ActionButton>
                                    <ActionButton
                                        size="small"
                                        confirm={`Cancel invite for ${row.email}?`}
                                        action={async () => {
                                            await api!.facilityControllerCancelInvite(
                                                {
                                                    facilityId: selection.id,
                                                    email: row.email!,
                                                }
                                            );
                                            await new Promise((r) =>
                                                setTimeout(r, 3000)
                                            );
                                        }}
                                        successMessage="Invite cancelled"
                                    >
                                        Cancel
                                    </ActionButton>
                                </>
                            )}
                        </Space>
                    ),
                },
            ]}
        >
            <div style={{ padding: '10px 0' }}>
                <b>Invite new member:</b>
            </div>
            <Space.Compact block>
                <Input
                    type="email"
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Enter email address"
                />
                <ActionButton
                    action={async () => {
                        await api?.facilityControllerInviteUser({
                            facilityId: selection.id,
                            inviteCreateDto: {
                                emails: [inviteEmail],
                                clientId: WEBAPP_AUTH0_CLIENT_ID,
                            },
                        });
                        await new Promise((r) => setTimeout(r, 3000));
                    }}
                    disabled={!inviteEmail.length}
                    successMessage="Facility membership invite sent"
                >
                    Send Invite
                </ActionButton>
            </Space.Compact>
        </FetchRecordTable>
    );
}
